import { Add, RemoveCircleOutline } from "@mui/icons-material";
import { Autocomplete, Button, Grid, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { lightGreyWithSmallIconCss } from "../../constants/selectCSS";
import { textFieldSX } from "../../constants/textFieldCSS";
import { ConditionsIcon } from "../../svgUrls";
import { ResponseConfigOption } from "../../types/common";
import { WorkflowNode } from "../../types/workflow";
import DropdownPopper from '../Common/DropdownPopper';

const conditionOptions = [
    { id: "0", title: "Equal" },
    { id: "1", title: "Not Equal" },
    { id: "2", title: "Greater Than" },
    { id: "3", title: "Less Than" },
    { id: "4", title: "Greater Than Or Equal" },
    { id: "5", title: "Less Than Or Equal" },
    { id: "6", title: "String Equal" },
    { id: "7", title: "String Not Equal" },
    { id: "8", title: "String Contains" },
    { id: "9", title: "String Not Contains" },
    { id: "10", title: "Starts With" },
    { id: "11", title: "Ends With" },
    { id: "12", title: "In" },
    { id: "13", title: "Not In" },
    { id: "14", title: "Is True" },
    { id: "15", title: "Is False" },
    { id: "16", title: "Is Null" },
    { id: "17", title: "Is Not Null" },
    { id: "18", title: "Is Empty" },
    { id: "19", title: "Is Not Empty" },
    { id: "20", title: "Contains Any" },
    { id: "21", title: "Contains All" },
    { id: "22", title: "Not Contains Any" },
    { id: "23", title: "Not Contains All" },
    { id: "24", title: "Regex Match" },
    { id: "25", title: "Regex Not Match" },
];

// TODO: fix any
type ExecutionConditionsProps = {
    openConditionsDetails: boolean,
    localConditions: any,
    removeCondition: any,
    setLocalConditions: any,
    responseConfig: any,
    isAnyStepIncomplete: boolean,
    setConditionsOpenDetails: (openDetails: boolean) => void,
    addNewCondition: () => void,
    onSave: (action?: string, cb?: () => void, isIntermittentSave?: boolean) => void,
    workflowNode: WorkflowNode,
    dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption>,
}

const ExecutionConditions = ({
    openConditionsDetails,
    localConditions,
    removeCondition,
    setLocalConditions,
    responseConfig,
    isAnyStepIncomplete,
    setConditionsOpenDetails,
    addNewCondition,
    onSave,
    workflowNode,
    dropdownOptionIdToOptionMap
}: ExecutionConditionsProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeConditionIndex, setActiveConditionIndex] = useState<number | null>(null);

    useEffect(() => {
        if (localConditions.length === 0) {
            addNewCondition();
        }
    }, []);

    const handleConditionValueChange = (index: number, option: ResponseConfigOption) => {
        const _conditions = [...localConditions];
        _conditions[index].node_data_key = option.id;
        _conditions[index].ref_node_id = option.refNodeId;
        setLocalConditions(_conditions);
        onSave("", () => {}, true);
    };

    return (
        <Grid
            container
            alignItems="center"
            spacing={1}
            className="!ml-0 !mt-2 mb-1"
        >
            <Grid
                key={-1}
                container
                alignItems="center"
                spacing={1}
                className="!ml-0 !mt-2 mb-1 !grid grid-cols-[calc(35%_-12px)_calc(30%_-12px)_calc(35%_-12px)_21px]"
            >
                <Grid item className="text-greyishBlueLight text-3xs">FIELD</Grid>
                <Grid item className="text-greyishBlueLight text-3xs">OPERATOR</Grid>
                <Grid item className="text-greyishBlueLight text-3xs">VALUE</Grid>
                <Grid item></Grid>
            </Grid>

            {localConditions.map((executionCondition: any, index: number) => {
                if (executionCondition.node_connection.end_node.id === workflowNode.node.id) {
                    return (
                        <Grid
                            key={executionCondition.id}
                            container
                            alignItems="center"
                            spacing={1}
                            className="!ml-0 !mt-2 mb-1 !grid grid-cols-[calc(35%_-12px)_calc(30%_-12px)_calc(35%_-12px)_21px]"
                        >
                            <Grid item className="text-greyishBlueLight text-3xs">
                                <TextField
                                    className="!text-xs"
                                    fullWidth
                                    size="small"
                                    value={`${executionCondition.node_data_key || ''}`}
                                    onClick={(e) => {
                                        setAnchorEl(e.currentTarget);
                                        setActiveConditionIndex(index);
                                    }}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>
                            <Grid item className="text-greyishBlueLight text-3xs">
                                <Select
                                    className="!text-xs"
                                    fullWidth
                                    size="small"
                                    value={executionCondition.condition}
                                    onChange={(e) => {
                                        const _conditions = [...localConditions];
                                        _conditions[index].condition = Number(e.target.value);
                                        setLocalConditions(_conditions);
                                        onSave("", () => {}, true);
                                    }}
                                    sx={{
                                        ...lightGreyWithSmallIconCss
                                    }}
                                >
                                    {conditionOptions.map((conditionOption) => (
                                        <MenuItem className="cursor-pointer" key={conditionOption.id} value={conditionOption.id}>
                                            {conditionOption.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item className="text-greyishBlueLight text-3xs">
                                <Autocomplete
                                    freeSolo
                                    options={[]}
                                    value={executionCondition.condition_check_key || executionCondition.condition_check_data}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{ ...textFieldSX }}
                                            size="small"
                                            onChange={(e) => {
                                                const _conditions = [...localConditions];
                                                _conditions[index].condition_check_data = e.target.value;
                                                setLocalConditions(_conditions);
                                            }}
                                            onBlur={() => onSave("", () => {}, true)}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item>
                                <RemoveCircleOutline
                                    className="text-greyishBlueLight cursor-pointer"
                                    onClick={() => {
                                        removeCondition(index);
                                        onSave("", () => {}, true);
                                    }}
                                />
                            </Grid>
                        </Grid>
                    );
                }
                return null;
            })}

            {Boolean(anchorEl) && (
                <DropdownPopper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    options={responseConfig}
                    onClose={() => {
                        setAnchorEl(null);
                    }}
                    onOptionClick={(option) => {
                        if (activeConditionIndex !== null) {
                            handleConditionValueChange(activeConditionIndex, option);
                        }
                    }}
                    // headerText="Select Condition Value"
                    groupByKey="refNodeId"
                />
            )}

            <Grid container justifyContent="space-between" className="mt-4">
                <Button
                    startIcon={<Add />}
                    onClick={addNewCondition}
                    variant="outlined"
                    size="small"
                >
                    Add Condition
                </Button>
            </Grid>
        </Grid>
    );
};

export default ExecutionConditions;